/**
 * HeroBlock: `hero` content block.
 *
 * data: {
 *   heading, subheading?, backgroundImageUrl?,
 *   ctaText?, ctaLink?,                   // primary CTA
 *   secondaryCtaText?, secondaryCtaLink?,  // optional second CTA
 *   audienceItems?: [{ label, description }], // audience bullet list
 *   showGraphPreview?: boolean            // live network graph in right col
 * }
 *
 * A full-bleed banner. When a background image is supplied, its URL is pushed
 * onto a CSS custom property (`--content-hero-image`) via a ref callback,
 * NOT an inline `style` object, so the no-inline-CSS repo rule holds even
 * for author-supplied URLs. Content.css consumes the custom property.
 *
 * Degrades gracefully: with no `heading` the block renders nothing; a missing
 * or unsafe CTA text/link simply omits that button. Up to two CTAs render as
 * a centered button row (mirrors the platform apex hero).
 *
 * showGraphPreview only activates when `useCapability('networkGraph')` is true;
 * the right column is absent otherwise.
 */
import React, { useCallback, Suspense } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Container, Button, Row, Col, Spinner } from 'react-bootstrap';

import useCapability from '@/shared/hooks/useCapability';

import {
    isAllowedLinkUrl,
    isAllowedImageUrl,
    isRawAnchorUrl,
    isExternalHttpUrl,
    cssEscape,
} from '../utils/urlValidator';

const EcosystemGraphPreview = React.lazy(
    () => import('@/features/graph/components/EcosystemGraphPreview')
);

// A single hero CTA. Renders nothing unless text + a safe link are present.
// Absolute http(s)/mailto/tel render as `<a href>` (http(s) externals get
// new-tab + noopener); internal relative paths route through React Router's
// <Link> so the tenant basePath is applied (round-4 review finding 8).
function HeroCta({ text, link, variant }) {
    if (!text || !link || !isAllowedLinkUrl(link)) {
        return null;
    }
    if (isRawAnchorUrl(link)) {
        return (
            <Button
                as="a"
                href={link}
                {...(isExternalHttpUrl(link) && {
                    target: '_blank',
                    rel: 'noopener noreferrer',
                })}
                variant={variant}
                size="lg"
                className="rounded-pill px-4"
            >
                {text}
            </Button>
        );
    }
    return (
        <Button
            as={Link}
            to={link}
            variant={variant}
            size="lg"
            className="rounded-pill px-4"
        >
            {text}
        </Button>
    );
}

HeroCta.propTypes = {
    text: PropTypes.string,
    link: PropTypes.string,
    variant: PropTypes.string.isRequired,
};

export default function HeroBlock({ data }) {
    const {
        heading,
        subheading,
        backgroundImageUrl,
        ctaText,
        ctaLink,
        secondaryCtaText,
        secondaryCtaLink,
        audienceItems,
        showGraphPreview,
    } = data || {};

    const networkGraphEnabled = useCapability('networkGraph');
    // Only show the graph column when the author opted in AND the ecosystem
    // has the networkGraph feature enabled.
    const showGraph = showGraphPreview && networkGraphEnabled;

    // Reject anything that is not an http(s) URL or a same-site relative
    // path. A bad image URL would otherwise be CSS-injected below, even
    // with quoting, a `data:` URL would let an admin embed multi-MB
    // payloads in the CSSOM (round-2 review finding 4 + 8).
    const hasImage = Boolean(
        backgroundImageUrl && isAllowedImageUrl(backgroundImageUrl)
    );

    // Ref callback: set the author-supplied background URL on a CSS custom
    // property once the node mounts. This keeps dynamic author data out of an
    // inline `style` object while still letting Content.css render the image.
    // The URL is CSS.escape'd to defang a crafted URL that closes the
    // url("...") string and opens new declarations (round-2 review finding 4:
    // CSS injection / cross-origin background-image leak).
    const setHeroRef = useCallback(
        (node) => {
            if (node && hasImage) {
                node.style.setProperty(
                    '--content-hero-image',
                    `url("${cssEscape(backgroundImageUrl)}")`
                );
            }
        },
        [hasImage, backgroundImageUrl]
    );

    // A hero with no heading is a half-authored block: render nothing.
    if (!heading) {
        return null;
    }

    const sectionClass = `content-hero text-white py-5${
        hasImage ? ' content-hero--has-image' : ''
    }`;

    // Render the CTA row only when at least one CTA passes the URL allowlist
    // (round-2 review finding 3: XSS via javascript: URLs).
    const hasPrimaryCta =
        Boolean(ctaText) && Boolean(ctaLink) && isAllowedLinkUrl(ctaLink);
    const hasSecondaryCta =
        Boolean(secondaryCtaText) &&
        Boolean(secondaryCtaLink) &&
        isAllowedLinkUrl(secondaryCtaLink);

    const ctaJustify = showGraph
        ? 'justify-content-start'
        : 'justify-content-center';

    const textContent = (
        <>
            {/* text-white on the h1 itself: the section is text-white, but
                the global heading-color rule overrides the inherited white
                on an h1, rendering it dark-navy on the dark hero. */}
            <h1 className="fw-bold mb-3 text-white">{heading}</h1>
            {subheading && <p className="lead mb-4 opacity-90">{subheading}</p>}
            {audienceItems?.length > 0 && (
                <ul className="list-unstyled mb-3 mb-lg-4 hero-audience-list">
                    {audienceItems.map((item, i) => (
                        <li key={i} className="mb-2">
                            <strong>{item.label}</strong>
                            {item.description && (
                                <>&nbsp;&mdash; {item.description}</>
                            )}
                        </li>
                    ))}
                </ul>
            )}
            {(hasPrimaryCta || hasSecondaryCta) && (
                <div className={`d-flex flex-wrap ${ctaJustify} gap-3`}>
                    <HeroCta text={ctaText} link={ctaLink} variant="accent" />
                    <HeroCta
                        text={secondaryCtaText}
                        link={secondaryCtaLink}
                        variant="outline-light"
                    />
                </div>
            )}
        </>
    );

    return (
        <section className={sectionClass} ref={setHeroRef}>
            <Container className="content-hero__inner py-5">
                <Row
                    className={
                        showGraph
                            ? 'align-items-center'
                            : 'justify-content-center'
                    }
                >
                    <Col
                        lg={showGraph ? 7 : 12}
                        className={showGraph ? '' : 'text-center'}
                    >
                        {textContent}
                    </Col>
                    {showGraph && (
                        <Col lg={5} className="d-none d-lg-block">
                            <div className="text-center">
                                <Suspense
                                    fallback={
                                        <div className="d-flex align-items-center justify-content-center bg-white rounded-circle mx-auto graph-preview-placeholder">
                                            <Spinner
                                                animation="border"
                                                variant="primary"
                                                size="sm"
                                            />
                                        </div>
                                    }
                                >
                                    <EcosystemGraphPreview height={300} />
                                </Suspense>
                            </div>
                        </Col>
                    )}
                </Row>
            </Container>
        </section>
    );
}

HeroBlock.propTypes = {
    data: PropTypes.shape({
        heading: PropTypes.string,
        subheading: PropTypes.string,
        backgroundImageUrl: PropTypes.string,
        ctaText: PropTypes.string,
        ctaLink: PropTypes.string,
        secondaryCtaText: PropTypes.string,
        secondaryCtaLink: PropTypes.string,
        audienceItems: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string,
                description: PropTypes.string,
            })
        ),
        showGraphPreview: PropTypes.bool,
    }),
};

HeroBlock.defaultProps = {
    data: {},
};
